// 32 bundled, open-licensed fonts (SIL OFL / Apache 2.0), self-hosted through @fontsource so they work offline.
// The first eight are the fonts the reference product's website demonstrates; the rest are MemoNest's choice.

export interface FontDef {
  id: string;
  label: string;
  family: string;
  /** CSS fallback stack appended after the family. */
  fallback: string;
  group: 'Handwriting' | 'Serif' | 'Sans' | 'Mono & pixel' | 'Script' | 'Japanese' | 'Korean' | 'System' | 'Custom';
  reference?: boolean;
  /** Visual size correction so different fonts look similar at the same size. */
  scale: number;
}

const hand = "'Segoe Print', 'Bradley Hand', 'Comic Sans MS', cursive";
const serif = "Georgia, 'Times New Roman', serif";
const sans = "-apple-system, 'Segoe UI', Roboto, sans-serif";
const mono = "ui-monospace, 'Cascadia Mono', Menlo, monospace";

export const FONTS: FontDef[] = [
  { id: 'schoolbell', label: 'Schoolbell', family: 'Schoolbell', fallback: hand, group: 'Handwriting', reference: true, scale: 1 },
  { id: 'caveat', label: 'Caveat', family: 'Caveat', fallback: hand, group: 'Handwriting', reference: true, scale: 1.15 },
  { id: 'mynerve', label: 'Mynerve', family: 'Mynerve', fallback: hand, group: 'Handwriting', reference: true, scale: 1 },
  { id: 'gloria', label: 'Gloria Hallelujah', family: 'Gloria Hallelujah', fallback: hand, group: 'Handwriting', reference: true, scale: 0.86 },
  { id: 'homemade-apple', label: 'Homemade Apple', family: 'Homemade Apple', fallback: hand, group: 'Script', reference: true, scale: 0.78 },
  { id: 'tiny5', label: 'Tiny5', family: 'Tiny5', fallback: mono, group: 'Mono & pixel', reference: true, scale: 0.95 },
  { id: 'dotgothic', label: 'DotGothic16', family: 'DotGothic16', fallback: mono, group: 'Mono & pixel', reference: true, scale: 0.9 },
  { id: 'noto-serif', label: 'Noto Serif', family: 'Noto Serif', fallback: serif, group: 'Serif', reference: true, scale: 0.86 },
  { id: 'patrick-hand', label: 'Patrick Hand', family: 'Patrick Hand', fallback: hand, group: 'Handwriting', scale: 1.02 },
  { id: 'indie-flower', label: 'Indie Flower', family: 'Indie Flower', fallback: hand, group: 'Handwriting', scale: 0.95 },
  { id: 'kalam', label: 'Kalam', family: 'Kalam', fallback: hand, group: 'Handwriting', scale: 0.95 },
  { id: 'shadows', label: 'Shadows Into Light', family: 'Shadows Into Light', fallback: hand, group: 'Handwriting', scale: 1.02 },
  { id: 'marker', label: 'Permanent Marker', family: 'Permanent Marker', fallback: hand, group: 'Handwriting', scale: 0.86 },
  { id: 'architects', label: "Architect's Daughter", family: 'Architects Daughter', fallback: hand, group: 'Handwriting', scale: 0.9 },
  { id: 'coming-soon', label: 'Coming Soon', family: 'Coming Soon', fallback: hand, group: 'Handwriting', scale: 0.88 },
  { id: 'reenie', label: 'Reenie Beanie', family: 'Reenie Beanie', fallback: hand, group: 'Handwriting', scale: 1.3 },
  { id: 'nothing', label: 'Nothing You Could Do', family: 'Nothing You Could Do', fallback: hand, group: 'Script', scale: 0.82 },
  { id: 'short-stack', label: 'Short Stack', family: 'Short Stack', fallback: hand, group: 'Handwriting', scale: 0.82 },
  { id: 'pacifico', label: 'Pacifico', family: 'Pacifico', fallback: hand, group: 'Script', scale: 0.82 },
  { id: 'dancing', label: 'Dancing Script', family: 'Dancing Script', fallback: hand, group: 'Script', scale: 1.05 },
  { id: 'inter', label: 'Inter', family: 'Inter', fallback: sans, group: 'Sans', scale: 0.88 },
  { id: 'nunito', label: 'Nunito', family: 'Nunito', fallback: sans, group: 'Sans', scale: 0.92 },
  { id: 'lora', label: 'Lora', family: 'Lora', fallback: serif, group: 'Serif', scale: 0.9 },
  { id: 'playfair', label: 'Playfair Display', family: 'Playfair Display', fallback: serif, group: 'Serif', scale: 0.9 },
  { id: 'space-mono', label: 'Space Mono', family: 'Space Mono', fallback: mono, group: 'Mono & pixel', scale: 0.8 },
  { id: 'vt323', label: 'VT323', family: 'VT323', fallback: mono, group: 'Mono & pixel', scale: 1.15 },
  { id: 'press-start', label: 'Press Start 2P', family: "'Press Start 2P'", fallback: mono, group: 'Mono & pixel', scale: 0.6 },
  { id: 'yomogi', label: 'Yomogi よもぎ', family: 'Yomogi', fallback: hand, group: 'Japanese', scale: 0.95 },
  { id: 'klee', label: 'Klee One クレー', family: 'Klee One', fallback: serif, group: 'Japanese', scale: 0.92 },
  { id: 'zen-maru', label: 'Zen Maru Gothic', family: 'Zen Maru Gothic', fallback: sans, group: 'Japanese', scale: 0.9 },
  { id: 'gaegu', label: 'Gaegu 개구', family: 'Gaegu', fallback: hand, group: 'Korean', scale: 1.08 },
  { id: 'nanum-pen', label: 'Nanum Pen 나눔펜', family: 'Nanum Pen Script', fallback: hand, group: 'Korean', scale: 1.25 },
];

export const SYSTEM_FONT: FontDef = { id: 'system', label: 'System', family: '-apple-system', fallback: sans, group: 'System', scale: 0.9 };

const custom = new Map<string, FontDef>();

export function registerCustomFont(id: string, family: string): FontDef {
  const def: FontDef = { id, label: family, family: `'${family.replace(/'/g, '')}'`, fallback: hand, group: 'Custom', scale: 1 };
  custom.set(id, def);
  return def;
}

export function unregisterCustomFont(id: string): void {
  custom.delete(id);
}

export function allFonts(): FontDef[] {
  return [...FONTS, SYSTEM_FONT, ...custom.values()];
}

export function getFont(id: string | undefined): FontDef {
  if (!id) return FONTS[0];
  return FONTS.find((f) => f.id === id) ?? (id === 'system' ? SYSTEM_FONT : custom.get(id)) ?? FONTS[0];
}

export function fontStack(id: string | undefined): string {
  const f = getFont(id);
  const fam = /^['"-]/.test(f.family) ? f.family : `'${f.family}'`;
  return `${fam}, ${f.fallback}`;
}
